import { ArrowRight, Check } from 'lucide-react';

/**
 * InvestmentCTA - Premium investment call-to-action section
 * Used in HomePage for AAC Real Estate I promotion
 */
const InvestmentCTA = ({
  title = 'Invest in "AAC Real Estate I"',
  returnTarget = '+20%',
  subtitle = 'Access institutional-quality real estate deals with transparency, data-driven insights, and a community you can trust.',
  features = [],
  ctaText = 'Invest',
  ctaHref = '/invest',
  imageSrc,
  imageAlt = 'Investment'
}) => {
  return (
    <section className="py-12 md:py-24 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Image - On top for mobile, left for desktop */}
          <div className="relative order-1">
            <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/20 to-purple-500/20 rounded-2xl blur-2xl opacity-50 hidden lg:block"></div>
            <img 
              src={imageSrc}
              alt={imageAlt}
              className="relative w-full h-auto rounded-xl shadow-2xl"
            />
            {/* Return Target below image - Desktop only */}
            <div className="hidden lg:flex items-center justify-center gap-2 mt-6">
              <span className="text-3xl font-bold text-yellow-500">{returnTarget}</span>
              <span className="text-gray-400">Annual Target</span>
            </div>
          </div>
          
          {/* Content */}
          <div className="text-white order-2 text-center lg:text-left">
            <p className="text-xs md:text-sm tracking-[0.2em] text-yellow-500 mb-3 md:mb-4">FLAGSHIP FUND</p>
            
            {/* Title - responsive */}
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 md:mb-4 leading-tight">
              {title}
            </h2>
            
            <p className="text-sm md:text-base lg:text-lg text-gray-300 mb-4 md:mb-6 leading-relaxed">
              {subtitle}
            </p>
            
            {features.length > 0 && (
              <div className="space-y-2 md:space-y-3 lg:space-y-4 mb-6 md:mb-8 text-left">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-start gap-2 md:gap-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-yellow-500/20 flex items-center justify-center mt-0.5">
                      <Check className="text-yellow-500" size={12} />
                    </div>
                    <span className="text-gray-200 text-xs md:text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            )}
            
            {/* Return Target - Mobile only, after features */}
            <div className="flex items-center justify-center lg:justify-start gap-2 mb-6 lg:hidden">
              <span className="text-2xl font-bold text-yellow-500">{returnTarget}</span>
              <span className="text-gray-400 text-sm">Annual Target</span>
            </div>
            
            {/* Button - centered on mobile */}
            <div className="flex justify-center lg:justify-start">
              <a 
                href={ctaHref}
                className="inline-flex items-center bg-white text-gray-900 px-8 py-4 font-bold hover:bg-yellow-400 transition-all duration-300 rounded-lg shadow-lg hover:shadow-xl text-sm md:text-base"
              >
                {ctaText}
                <ArrowRight className="ml-2" size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InvestmentCTA;
